import { Injectable } from '@angular/core'

import { NumberDate } from '@lemon/core/model/common'

import { FirebaseLemonator } from 'app/_angular/service/firebase-lemonator.service'
import { KirjanpitajanTiimi, KirjanpitajanTiimikokonaisuus, KirjanpitajanTiimikokonaisuusVarastossa, KirjanpitajanTiimiTiimikokonaisuudessa, KirjanpitajanTiimiVarastossa, KirjanpitajanTiiminJasen, KirjanpitajienTiimit } from 'app/_jaettu-lemonator/model/kirjanpitaja'
import { YllapitoUriService } from 'app/_jaettu-lemonator/service/yllapito-uri.service'

export type TiimiRooli = 'esihenkilo' | 'jasen'

// Sentinel-arvo alkupäivälle: "voimassa aikojen alusta saakka". Tallennetaan dokumenttiin koska tietomallissa
// alku on pakollinen NumberDate, mutta käyttäjälle esitetään tyhjänä sekä dialogeissa että listauksissa.
export const ALKU_AIKOJEN_ALUSTA: NumberDate = 100101

export interface JaksonMuutos {
  alku: NumberDate
  loppu: NumberDate | null
  primary: boolean
}

@Injectable()
export class TiimienHallintaService {
  constructor(
    private _firebase: FirebaseLemonator,
    private _yllapitoUriService: YllapitoUriService,
  ) {}

  async muokkaaTiiminJaksoa(tiimiAvain: KirjanpitajanTiimi['avain'], rooli: TiimiRooli, kirjanpitajaAvain: string, nykyinenAlku: NumberDate, uusi: JaksonMuutos): Promise<void> {
    const dokumentti = await this._lataaTuore()
    const tiimi = dokumentti.tiimit[tiimiAvain]
    if (!tiimi) {
      throw new Error('Tiimiä ei löytynyt avaimella ' + tiimiAvain)
    }
    const kentta = rooli === 'esihenkilo' ? 'esihenkilot' : 'jasenet'
    const paivitetty = this._paivitaJakso(tiimi[kentta][kirjanpitajaAvain], kirjanpitajaAvain, nykyinenAlku, uusi)
    const polku = `tiimit.${tiimiAvain}.${kentta}.${kirjanpitajaAvain}`
    await this._kirjoita(polku, paivitetty)
  }

  async lisaaTiiminJasen(tiimiAvain: KirjanpitajanTiimi['avain'], rooli: TiimiRooli, jakso: KirjanpitajanTiiminJasen): Promise<void> {
    const dokumentti = await this._lataaTuore()
    const tiimi = dokumentti.tiimit[tiimiAvain]
    if (!tiimi) {
      throw new Error('Tiimiä ei löytynyt avaimella ' + tiimiAvain)
    }
    const kentta = rooli === 'esihenkilo' ? 'esihenkilot' : 'jasenet'
    const olemassaolevat = tiimi[kentta][jakso.kirjanpitajaAvain] ?? []
    const paivitetty = [...olemassaolevat, jakso].sort((a, b) => a.alku - b.alku)
    const polku = `tiimit.${tiimiAvain}.${kentta}.${jakso.kirjanpitajaAvain}`
    await this._kirjoita(polku, paivitetty)
  }

  async muokkaaKokonaisuudenJaksoa(kokonaisuusAvain: KirjanpitajanTiimikokonaisuus['avain'], kirjanpitajaAvain: string, nykyinenAlku: NumberDate, uusi: JaksonMuutos): Promise<void> {
    const dokumentti = await this._lataaTuore()
    const kokonaisuus = dokumentti.tiimikokonaisuudet[kokonaisuusAvain]
    if (!kokonaisuus) {
      throw new Error('Kokonaisuutta ei löytynyt avaimella ' + kokonaisuusAvain)
    }
    const paivitetty = this._paivitaJakso(kokonaisuus.esihenkilot[kirjanpitajaAvain], kirjanpitajaAvain, nykyinenAlku, uusi)
    const polku = `tiimikokonaisuudet.${kokonaisuusAvain}.esihenkilot.${kirjanpitajaAvain}`
    await this._kirjoita(polku, paivitetty)
  }

  async luoTiimi(avain: KirjanpitajanTiimi['avain'], tiedot: { nimi: string; alku: NumberDate }): Promise<void> {
    const dokumentti = await this._lataaTuore()
    if (dokumentti.tiimit?.[avain]) {
      throw new Error('Tiimi avaimella ' + avain + ' on jo olemassa')
    }
    const uusi: KirjanpitajanTiimiVarastossa = {
      avain,
      nimi: tiedot.nimi,
      alku: tiedot.alku,
      loppu: null,
      esihenkilot: {},
      jasenet: {},
    }
    const uri = this._yllapitoUriService.annaKirjanpitajienTiimitUri()
    await this._firebase.firestoreUpdateData(uri, { [`tiimit.${avain}`]: uusi })
  }

  async paivitaTiiminTiedot(tiimiAvain: KirjanpitajanTiimi['avain'], tiedot: { nimi: string; alku: NumberDate; loppu: NumberDate | null }): Promise<void> {
    const uri = this._yllapitoUriService.annaKirjanpitajienTiimitUri()
    const paivitykset: Record<string, unknown> = {
      [`tiimit.${tiimiAvain}.nimi`]: tiedot.nimi,
      [`tiimit.${tiimiAvain}.alku`]: tiedot.alku,
      [`tiimit.${tiimiAvain}.loppu`]: tiedot.loppu,
    }
    await this._firebase.firestoreUpdateData(uri, paivitykset)
  }

  async paivitaTiimikokonaisuudenTiedot(
    kokonaisuusAvain: KirjanpitajanTiimikokonaisuus['avain'],
    tiedot: { nimi: string; alku: NumberDate; loppu: NumberDate | null },
  ): Promise<void> {
    const uri = this._yllapitoUriService.annaKirjanpitajienTiimitUri()
    const paivitykset: Record<string, unknown> = {
      [`tiimikokonaisuudet.${kokonaisuusAvain}.nimi`]: tiedot.nimi,
      [`tiimikokonaisuudet.${kokonaisuusAvain}.alku`]: tiedot.alku,
      [`tiimikokonaisuudet.${kokonaisuusAvain}.loppu`]: tiedot.loppu,
    }
    await this._firebase.firestoreUpdateData(uri, paivitykset)
  }

  async luoTiimikokonaisuus(
    avain: KirjanpitajanTiimikokonaisuus['avain'],
    tiedot: { nimi: string; alku: NumberDate },
  ): Promise<void> {
    const dokumentti = await this._lataaTuore()
    if (dokumentti.tiimikokonaisuudet?.[avain]) {
      throw new Error('Tiimikokonaisuus avaimella ' + avain + ' on jo olemassa')
    }
    const uusi: KirjanpitajanTiimikokonaisuusVarastossa = {
      avain,
      nimi: tiedot.nimi,
      alku: tiedot.alku,
      loppu: null,
      esihenkilot: {},
      tiimit: [],
    }
    const uri = this._yllapitoUriService.annaKirjanpitajienTiimitUri()
    await this._firebase.firestoreUpdateData(uri, { [`tiimikokonaisuudet.${avain}`]: uusi })
  }

  async lisaaTiimiKokonaisuuteen(
    kokonaisuusAvain: KirjanpitajanTiimikokonaisuus['avain'],
    jakso: KirjanpitajanTiimiTiimikokonaisuudessa,
  ): Promise<void> {
    const dokumentti = await this._lataaTuore()
    const kokonaisuus = dokumentti.tiimikokonaisuudet[kokonaisuusAvain]
    if (!kokonaisuus) {
      throw new Error('Kokonaisuutta ei löytynyt avaimella ' + kokonaisuusAvain)
    }
    const olemassaolevat = kokonaisuus.tiimit ?? []
    const paivitetty = [...olemassaolevat, jakso].sort((a, b) => a.alku - b.alku)
    await this._kirjoitaKokonaisuudenTiimit(kokonaisuusAvain, paivitetty)
  }

  async muokkaaKokonaisuudenTiiminJaksoa(
    kokonaisuusAvain: KirjanpitajanTiimikokonaisuus['avain'],
    tiimiAvain: KirjanpitajanTiimi['avain'],
    nykyinenAlku: NumberDate,
    uusi: { alku: NumberDate; loppu: NumberDate | null },
  ): Promise<void> {
    const dokumentti = await this._lataaTuore()
    const kokonaisuus = dokumentti.tiimikokonaisuudet[kokonaisuusAvain]
    if (!kokonaisuus) {
      throw new Error('Kokonaisuutta ei löytynyt avaimella ' + kokonaisuusAvain)
    }
    const olemassaolevat = kokonaisuus.tiimit ?? []
    const indeksi = olemassaolevat.findIndex(t => t.avain === tiimiAvain && t.alku === nykyinenAlku)
    if (indeksi < 0) {
      throw new Error('Tiimin jaksoa ei löytynyt avaimella ' + tiimiAvain + ' ja alkupäivällä ' + nykyinenAlku)
    }
    const paivitetty = [...olemassaolevat]
    paivitetty[indeksi] = { avain: tiimiAvain, alku: uusi.alku, loppu: uusi.loppu }
    paivitetty.sort((a, b) => a.alku - b.alku)
    await this._kirjoitaKokonaisuudenTiimit(kokonaisuusAvain, paivitetty)
  }

  async lisaaKokonaisuudenEsihenkilo(kokonaisuusAvain: KirjanpitajanTiimikokonaisuus['avain'], jakso: KirjanpitajanTiiminJasen): Promise<void> {
    const dokumentti = await this._lataaTuore()
    const kokonaisuus = dokumentti.tiimikokonaisuudet[kokonaisuusAvain]
    if (!kokonaisuus) {
      throw new Error('Kokonaisuutta ei löytynyt avaimella ' + kokonaisuusAvain)
    }
    const olemassaolevat = kokonaisuus.esihenkilot[jakso.kirjanpitajaAvain] ?? []
    const paivitetty = [...olemassaolevat, jakso].sort((a, b) => a.alku - b.alku)
    const polku = `tiimikokonaisuudet.${kokonaisuusAvain}.esihenkilot.${jakso.kirjanpitajaAvain}`
    await this._kirjoita(polku, paivitetty)
  }

  // Jos kirjanpitäjällä on useita jaksoja, kohdistetaan päivitys oikeaan nykyisen alkupäivän perusteella
  private _paivitaJakso(jaksot: KirjanpitajanTiiminJasen[] | undefined, kirjanpitajaAvain: string, nykyinenAlku: NumberDate, uusi: JaksonMuutos): KirjanpitajanTiiminJasen[] {
    if (!jaksot || jaksot.length === 0) {
      throw new Error('Henkilölle ei ole tallennettuja jaksoja')
    }
    const indeksi = jaksot.findIndex(j => j.alku === nykyinenAlku)
    if (indeksi < 0) {
      throw new Error('Jaksoa ei löytynyt alkupäivällä ' + nykyinenAlku)
    }
    const uusiJakso: KirjanpitajanTiiminJasen = { kirjanpitajaAvain, alku: uusi.alku, loppu: uusi.loppu }
    if (uusi.primary) {
      uusiJakso.primary = true
    }
    const kopio = [...jaksot]
    kopio[indeksi] = uusiJakso
    return kopio.sort((a, b) => a.alku - b.alku)
  }

  private async _lataaTuore(): Promise<KirjanpitajienTiimit> {
    const uri = this._yllapitoUriService.annaKirjanpitajienTiimitUri()
    const dokumentti = await this._firebase.firestoreDoc<KirjanpitajienTiimit>(uri).get()
    if (!dokumentti) {
      throw new Error('Firestoren dokumenttia ' + uri + ' ei löytynyt')
    }
    return dokumentti
  }

  private async _kirjoita(polku: string, arvo: KirjanpitajanTiiminJasen[]): Promise<void> {
    const uri = this._yllapitoUriService.annaKirjanpitajienTiimitUri()
    const fieldPath = this._firebase.firestoreFieldPath(...polku.split('.'))
    await this._firebase.firestoreUpdateFields<KirjanpitajanTiiminJasen[]>(uri, { fieldPath, data: arvo })
  }

  private async _kirjoitaKokonaisuudenTiimit(kokonaisuusAvain: KirjanpitajanTiimikokonaisuus['avain'], arvo: KirjanpitajanTiimiTiimikokonaisuudessa[]): Promise<void> {
    const uri = this._yllapitoUriService.annaKirjanpitajienTiimitUri()
    const fieldPath = this._firebase.firestoreFieldPath('tiimikokonaisuudet', kokonaisuusAvain, 'tiimit')
    await this._firebase.firestoreUpdateFields<KirjanpitajanTiimiTiimikokonaisuudessa[]>(uri, { fieldPath, data: arvo })
  }
}
